<template>
  <b-modal id="print-modal" size="lg" scrollable @shown="open" @hidden="reset">
    <template v-slot:modal-header="{ close }">
      <b-container>
        <div class="modal-title">
          <h3 class="text-bold text-center">Job Docket</h3>
          <button type="button" aria-label="Close" class="close" @click="close">
            <i class="di-remove-10"></i>
          </button>
        </div>
      </b-container>
    </template>

    <div v-if="outdated" class="print-notice print-notice--warning">
      <div class="print-notice-text">
        IO has been updated since this page was opened. Reload to get the latest printing.
      </div>
      <b-button size="sm" variant="primary" @click="reloadPage">Reload</b-button>
    </div>

    <div v-if="askingPermission" class="print-notice print-notice--warning">
      <div class="print-notice-text">
        Chrome is asking whether IO may connect to Dresden Print on this computer. Click
        <strong>Allow</strong> in the box at the top left of the browser window.
      </div>
    </div>

    <div v-if="notice" class="print-notice" :class="`print-notice--${notice.variant}`">
      <div class="print-notice-text">{{ notice.text }}</div>
    </div>

    <div v-if="showSetup" class="setup">
      <template v-if="problem === 'too-old'">
        <div class="setup-title">This computer is too old for Dresden Print</div>
        <p class="setup-text">
          Dresden Print needs Windows 7 or newer, or macOS 10.12 or newer. Ask IT to set up
          printing on a newer computer.
        </p>
      </template>

      <template v-else-if="problem === 'blocked'">
        <div class="setup-title">The browser is blocking IO from Dresden Print</div>
        <ol class="setup-steps">
          <li>Click the icon at the left of the address bar, then <strong>Site settings</strong>.</li>
          <li>Set <strong>Local network access</strong> (it may be called <strong>Apps on device</strong>) to <strong>Allow</strong>.</li>
          <li>Come back here and press <strong>Done, check again</strong>.</li>
        </ol>
      </template>

      <template v-else>
        <template v-if="problem === 'not-running'">
          <div class="setup-title">Dresden Print is installed but not running</div>
          <p class="setup-text">
            Open it from {{ os === 'macos' ? 'Applications' : 'the Dresden Print icon on the desktop or in the Start menu' }},
            then press <strong>Done, check again</strong>. If it does not start, install it again below.
          </p>
        </template>
        <template v-else>
          <div class="setup-title">Set up this computer for printing</div>
          <p class="setup-text">
            A one-time setup, about a minute. After this, Print sends the docket
            straight to the counter printer. Already installed? Open
            <strong>Dresden Print</strong> from {{ os === 'macos' ? 'Applications' : 'the desktop or the Start menu' }}
            and press <strong>Done, check again</strong>.
          </p>
        </template>

        <a class="setup-download" :href="installerUrl">
          Download the installer
          <span class="setup-download-sub">{{ os === 'macos' ? 'for Mac' : 'for Windows' }}</span>
        </a>
        <b-link class="setup-other" @click="platform = os === 'macos' ? 'windows' : 'macos'">
          Need the {{ os === 'macos' ? 'Windows' : 'Mac' }} installer instead?
        </b-link>

        <ol v-if="os === 'macos'" class="setup-steps">
          <li>Open the downloaded file and double-click <strong>Dresden Print</strong>. If your Mac asks, click <strong>Open</strong>.</li>
          <li>It installs itself. A printer icon appears at the top right of your screen.</li>
          <li>Plug the printer in and switch it on. The agent sets it up by itself. If it does not, click the icon and choose <strong>Find printer</strong>.</li>
          <li>It starts by itself when you log in. To open it again after quitting, find <strong>Dresden Print</strong> in Applications.</li>
        </ol>
        <ol v-else class="setup-steps">
          <li>If the browser says the file is not commonly downloaded, choose <strong>Keep</strong>.</li>
          <li>Open the downloaded <strong>Dresden Print</strong> file.</li>
          <li>If Windows shows a blue box, click <strong>More info</strong> then <strong>Run anyway</strong>.</li>
          <li>A small printer icon appears near the clock. That is all; it installs itself.</li>
          <li>Plug the printer in and switch it on. The agent sets it up by itself. If it does not, click the small printer icon near the clock and choose <strong>Find printer</strong>.</li>
          <li>It starts by itself when you log in. To open it again after quitting, use the <strong>Dresden Print</strong> icon on the desktop or in the Start menu. No need to run the installer again.</li>
        </ol>
      </template>

      <p v-if="failedChecks >= 2 && problem !== 'too-old'" class="setup-text setup-still">
        Still not reachable. Ask IT to check Dresden Print on this computer.
      </p>

      <div v-if="problem !== 'too-old'" class="setup-actions">
        <b-button size="sm" variant="primary" :disabled="checking" @click="recheck">
          {{ checking ? 'Checking...' : 'Done, check again' }}
        </b-button>
      </div>
    </div>

    <div class="docket-preview">
      <iframe
        v-if="docketUrl"
        ref="docketFrame"
        :src="docketUrl"
        class="docket-frame"
        :style="frameStyle"
        title="Job docket"
      ></iframe>
      <div v-else class="text-center text-muted py-5">Loading docket...</div>
    </div>

    <template v-slot:modal-footer="{ cancel }">
      <div class="flex-fill m-0">
        <div class="container">
          <div class="d-flex justify-content-between align-items-center">
            <b-button variant="outline-light" @click="cancel()">Close</b-button>
            <div class="d-flex align-items-center">
              <span v-if="agentPrinter" class="printer-name mr-3">{{ agentPrinter }}</span>
              <b-button variant="primary" :disabled="!docketUrl || printing" @click="print">
                {{ printing ? 'Printing...' : 'Print Job Docket' }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex';
import axios from 'axios';
import printingApi from '../../api/printing';
import {
  PrintResult,
  localAccess,
  print as printViaAgent,
  status as agentStatus,
  unsupportedSystem,
  whyUnreachable,
} from '../../support/printBridge';

const RECEIPT_WIDTH = 302; // 80mm at 96dpi
const SCALE = 1.9;

export default {
  name: 'PrintModal',
  data() {
    return {
      docketUrl: null,
      frameHeight: 760,
      printing: false,
      checking: false,
      notice: null,
      showSetup: false,
      agentPrinter: null,
      problem: null,
      failedChecks: 0,
      platform: null,
      askingPermission: false,
      outdated: false,
    };
  },
  computed: {
    ...mapState({
      selectedOrderId: state => state.selectedOrderId,
    }),
    installerUrl() {
      return printingApi.installerUrl(this.os);
    },
    // Picked from the browser, but staff can switch: a manager setting up a
    // Windows counter may be reading this on a Mac.
    os() {
      if (this.platform) return this.platform;
      return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? 'macos' : 'windows';
    },
    frameStyle() {
      return {
        width: `${RECEIPT_WIDTH}px`,
        height: `${this.frameHeight}px`,
        zoom: SCALE,
        border: '0',
        background: '#fff',
      };
    },
  },
  mounted() {
    window.addEventListener('message', this.onMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onMessage);
  },
  methods: {
    onMessage(e) {
      if (e.origin !== window.location.origin && !this.isApiOrigin(e.origin)) return;
      if (e.data && e.data.dresdenDocketHeight) {
        this.frameHeight = e.data.dresdenDocketHeight + 4;
      }
    },
    isApiOrigin(origin) {
      try {
        return origin === new URL(this.newAppApiUrl).origin;
      } catch (err) {
        return false;
      }
    },
    open() {
      this.loadDocket();
      this.checkOutdated();
      this.checkAgent();
    },
    // print=1 tells the API this is a real print (it writes the "printed on"
    // order note); the previews the modal loads on open must not.
    loadDocket() {
      this.docketUrl = null;
      this.frameHeight = 760;
      axios
        .get(`${this.newAppApiUrl}/staff/job-tray-signed-url/${this.selectedOrderId}`, {
          params: { design: 'new' },
        })
        .then(({ data }) => {
          this.docketUrl = data;
        });
    },
    // Staff should find out the agent is missing when the modal opens, not after
    // pressing Print and getting an error.
    async checkAgent() {
      this.checking = true;
      // Chrome holds the call while its permission prompt is open; tell staff
      // what to click instead of looking stuck.
      this.askingPermission = (await localAccess()) === 'prompt';
      const info = await agentStatus();
      this.askingPermission = false;
      this.checking = false;

      if (info && info.refused) {
        this.agentPrinter = null;
        this.showSetup = false;
        this.notice = {
          variant: 'error',
          text: `Dresden Print is running but refused ${info.origin}. It is an older version: download the installer again and open it, then press Done.`,
        };
        return false;
      }

      this.agentPrinter = info && info.printer ? info.printer : null;
      this.showSetup = !info;
      if (!info) {
        this.problem = unsupportedSystem() ? 'too-old' : await whyUnreachable();
      }

      return !!info;
    },
    async recheck() {
      this.notice = null;
      if (!(await this.checkAgent())) {
        this.failedChecks += 1;
        return;
      }
      this.notice = this.agentPrinter
        ? { variant: 'success', text: `Found it. Printing goes to ${this.agentPrinter}.` }
        : {
          variant: 'warning',
          text: 'Dresden Print is running but has no printer yet. Plug the printer in and switch it on, then click the printer icon and choose Find printer.',
        };
    },
    async print() {
      this.printing = true;
      this.notice = null;

      try {
        const { data: escposUrl } = await printingApi.escposUrl(this.selectedOrderId);
        const outcome = await printViaAgent(escposUrl);

        if (outcome.result === PrintResult.PRINTED) {
          this.agentPrinter = outcome.printer || this.agentPrinter;
          this.notice = {
            variant: 'success',
            text: `Printed on ${outcome.printer || 'the counter printer'}.`,
          };
          return;
        }

        if (outcome.result === PrintResult.NO_AGENT) {
          this.agentPrinter = null;
          this.showSetup = true;
          this.scrollModalTop();
          return;
        }

        this.notice = { variant: 'error', text: outcome.message };
        this.scrollModalTop();
      } catch (error) {
        this.notice = { variant: 'error', text: this.describe(error) };
        this.scrollModalTop();
      } finally {
        this.printing = false;
      }
    },
    // A bare "Not found" from an axios 404 tells staff nothing. Say what is
    // actually wrong so it is obvious whether to call someone.
    describe(error) {
      const status = error.response && error.response.status;

      if (status === 404) {
        return 'Printing is not available on this server yet. It needs the latest API release.';
      }
      if (status === 401) {
        return 'Your session has expired. Log in again, then print.';
      }
      if (status === 403) {
        return 'Your account is not allowed to print dockets. Ask a manager to check your role.';
      }
      if (status >= 500) {
        return 'The server could not prepare the docket. Try again in a moment; if it keeps failing, send this order number to IT.';
      }

      const message = error.response && error.response.data && error.response.data.message;

      return message || 'Could not prepare the docket.';
    },
    scrollModalTop() {
      this.$nextTick(() => {
        const modal = document.getElementById('print-modal');
        const body = modal && modal.querySelector('.modal-body');
        if (body) body.scrollTop = 0;
      });
    },
    // A tab left open across a release keeps running the old IO, with old
    // printing steps (the Geelong Mac on 9 Oct). Compare the app bundle this
    // page loaded with the one the server serves now.
    async checkOutdated() {
      try {
        const html = await (await fetch(`/?v=${Date.now()}`, { cache: 'no-store' })).text();
        const latest = html.match(/\/js\/app\.[a-z0-9]+(?:\.min)?\.js/);
        this.outdated = !!latest && !document.querySelector(`script[src*="${latest[0]}"]`);
      } catch (err) {
        this.outdated = false;
      }
    },
    reloadPage() {
      window.location.reload();
    },
    reset() {
      this.docketUrl = null;
      this.printing = false;
      this.checking = false;
      this.notice = null;
      this.showSetup = false;
      this.agentPrinter = null;
      this.problem = null;
      this.failedChecks = 0;
      this.platform = null;
      this.askingPermission = false;
      this.outdated = false;
    },
  },
};
</script>

<style scoped>
.docket-preview {
  display: flex;
  justify-content: center;
  background: #f0f1f4;
  padding: 16px;
  overflow-x: hidden;
}
.docket-frame {
  display: block;
}
.printer-name {
  font-size: 13px;
  color: #6b7683;
}
.print-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 12px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid transparent;
}
.print-notice-text {
  flex: 1;
}
.print-notice--success {
  border-color: #b7e0c6;
  background: #eaf7ef;
}
.print-notice--warning {
  border-color: #ffe08a;
  background: #fff9e6;
}
.print-notice--error {
  border-color: #f0b7b3;
  background: #fdeceb;
}
.setup {
  margin: 0 0 12px;
  padding: 14px 16px;
  border: 1px solid #ffe08a;
  background: #fff9e6;
  border-radius: 8px;
}
.setup-title {
  font-weight: 700;
  margin-bottom: 4px;
}
.setup-text {
  margin: 0 0 8px;
}
.setup-steps {
  margin: 0 0 12px;
  padding-left: 18px;
}
.setup-steps li {
  margin-bottom: 5px;
}
.setup-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.setup-download {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  background: #054f91;
  color: #fff;
  font-weight: 600;
  border-radius: 6px;
  padding: 10px 18px;
  margin: 4px 0 14px;
  text-decoration: none;
}
.setup-download:hover {
  background: #043c6e;
  color: #fff;
  text-decoration: none;
}
.setup-other {
  display: block;
  margin: -8px 0 12px;
  font-size: 13px;
}
.setup-still {
  font-weight: 600;
}
.setup-download-sub {
  font-weight: 400;
  font-size: 13px;
  opacity: 0.85;
}
</style>
